import * as React from 'react';
import { configConsumer } from '@estee/elc-service';
import { observer } from 'mobx-react';
import { ProductAccordion } from '../product-accordion/ProductAccordion';
import { IProductBriefCollection } from '../product-brief-collection/ProductBriefCollection';
import { BreakpointsWrapper } from '../breakpoints-wrapper/BreakpointsWrapper';
import ProductGrid, { Formatter } from './ProductGrid';
import { Breakpoints } from '@estee/elc-base-theme';
import { IBreakpoints } from '~interfaces/IBreakpoints';
import { GridItem } from '~domain/entities/GridItem';
import { IProductCarousel } from 'src/internal/views/product-carousel/ProductCarousel';
import { ProductGridHeader } from './ProductGridHeader';
import { ISliderSettings } from '@estee/elc-slider';

export interface IProductGridCarousel {
    config?: IProductGridCarouselConfig;
    positionGridInsideAccordion: boolean;
    isLoaded: boolean;
    gridItems: GridItem[];
    featuredProductId?: string;
    setGridPosition(): void;
    categoryTag: string;
    carouselSettings?: ISliderSettings;
}

interface IProductGridCarouselConfig {
    accordionDefaultOpen?: boolean;
    productGridBreakpoints: IBreakpoints;
    enableAccordion?: boolean;
}

const productAccordionChangeHandler = (
    setGridPosition: () => void,
    accordionDefaultOpen?: boolean
) => (openIndex: number) => {
    if (!accordionDefaultOpen && openIndex === 0) {
        setGridPosition();
    }
};

const buildBriefProps = (props: IProductGridCarousel) => {
    const { gridItems, featuredProductId, isLoaded, positionGridInsideAccordion } = props;

    const briefProps: IProductBriefCollection = {
        gridItems,
        isLoaded,
        featuredProductId,
        positionGridInsideAccordion
    };

    return briefProps;
};

export const ProductGridCarousel = configConsumer([
    'accordionDefaultOpen',
    'productGridBreakpoints',
    'enableAccordion'
])(
    observer(function ProductGridCarousel(props: IProductGridCarousel) {
        const {
            productGridBreakpoints,
            enableAccordion,
            accordionDefaultOpen
        } = props.config as IProductGridCarouselConfig;
        const {
            numColumnsMobile,
            numColumnsDesktop,
            numColumnsLargeScreen,
            numColumnsExtraLargeScreen
        } = productGridBreakpoints;

        const carouselSettings = props.carouselSettings || {
            dots: false,
            infinite: false,
            slidesToShow: numColumnsExtraLargeScreen,
            slidesToScroll: 1,
            responsive: [
                {
                    breakpoint: Breakpoints.extraLargeScreen,
                    settings: {
                        slidesToShow: numColumnsLargeScreen,
                        slidesToScroll: 1,
                        infinite: false,
                        dots: false
                    }
                },
                {
                    breakpoint: Breakpoints.largeScreen,
                    settings: {
                        slidesToShow: numColumnsDesktop,
                        slidesToScroll: 1,
                        infinite: false,
                        dots: false
                    }
                },
                {
                    breakpoint: Breakpoints.desktop,
                    settings: {
                        slidesToShow: numColumnsMobile,
                        slidesToScroll: 1,
                        infinite: false,
                        dots: false,
                        arrows: true
                    }
                }
            ]
        };

        const productCarouselProps: IProductCarousel['config'] = {
            carouselSettings,
            gridItems: props.gridItems
        };
        const { categoryTag, setGridPosition } = props;

        const productGrid = (
            <>
                <ProductGridHeader categoryTag={categoryTag} isWrappedByContainer={false} />
                <BreakpointsWrapper breakpoints={productGridBreakpoints}>
                    <ProductGrid
                        formatter={Formatter.CAROUSEL}
                        carouselProps={productCarouselProps}
                        briefProps={buildBriefProps(props)}
                    />
                </BreakpointsWrapper>
            </>
        );

        return enableAccordion ? (
            <ProductAccordion
                onChange={productAccordionChangeHandler(setGridPosition, accordionDefaultOpen)}
            >
                {productGrid}
            </ProductAccordion>
        ) : (
            productGrid
        );
    })
);
